import { useEffect, useState } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler, RadialLinearScale, ScatterController } from 'chart.js';
import { Line, Bar, Doughnut, Pie, Scatter, Radar } from 'react-chartjs-2';
import FacetedSearch from '../../components/FacetedSearch/FacetedSearch';
import FloatingToolbar from '../../components/FloatingToolbar/FloatingToolbar';
import MetabaseWidget from '../../components/MetabaseWidget';
import { fetchAllDashboardData } from '../../services/analyticsApi';
import './Dashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
  ScatterController
);

const Dashboard = ({ setActiveTab }) => {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showFacetedSearch, setShowFacetedSearch] = useState(false);
  const [showSliceModal, setShowSliceModal] = useState(false);
  const [activeSliceFilter, setActiveSliceFilter] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [filters, setFilters] = useState({
    poNumber: '',
    sender: '',
    receiver: '',
    epcCode: '',
    productCode: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });

  const [facetedFilters, setFacetedFilters] = useState({});

  // API Data State
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiSource, setApiSource] = useState('titan'); // 'titan', 'external', or 'rfxcel'

  useEffect(() => {
    setActiveTab('dashboard');
  }, [setActiveTab]);

  // Fetch dashboard data from API
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchAllDashboardData(apiSource);
        setDashboardData(data);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [apiSource]);

  // Chart configurations
  ChartJS.defaults.font.family = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  ChartJS.defaults.color = '#6b7280';

  // Shipment Frequency Chart Data
  const frequencyChartData = {
    labels: dashboardData?.shipmentFrequency?.labels || Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`),
    datasets: [{
      label: 'Daily Shipments',
      data: dashboardData?.shipmentFrequency?.values || Array.from({ length: 30 }, () => Math.floor(Math.random() * 100) + 200),
      borderColor: '#6366f1',
      backgroundColor: 'rgba(99, 102, 241, 0.1)',
      tension: 0.3,
      fill: true
    }]
  };

  const frequencyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  // Quantity Distribution Chart Data
  const quantityChartData = {
    labels: dashboardData?.quantityDistribution?.labels || ['0-100', '100-250', '250-500', '500-1000', '1000+'],
    datasets: [{
      label: 'Shipments',
      data: dashboardData?.quantityDistribution?.values || [45, 78, 125, 89, 34],
      backgroundColor: [
        'rgba(99, 102, 241, 0.8)',
        'rgba(139, 92, 246, 0.8)',
        'rgba(168, 85, 247, 0.8)',
        'rgba(196, 181, 253, 0.8)',
        'rgba(221, 214, 254, 0.8)'
      ]
    }]
  };

  const quantityChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  // Product Mix Chart Data
  const productMixData = {
    labels: dashboardData?.productMix?.labels || ['Electronics', 'Apparel', 'Food & Beverage', 'Industrial', 'Pharmaceuticals'],
    datasets: [{
      data: dashboardData?.productMix?.values || [35, 25, 20, 15, 5],
      backgroundColor: [
        '#6366f1',
        '#8b5cf6',
        '#a855f7',
        '#c084fc',
        '#e9d5ff'
      ]
    }]
  };

  const productMixOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 10,
          font: {
            size: 11
          }
        }
      }
    }
  };

  // Location Chart Data
  const locationChartData = {
    labels: dashboardData?.shipmentsByLocation?.labels || ['Los Angeles', 'New York', 'Chicago', 'Houston', 'Miami', 'Seattle', 'Boston', 'Dallas'],
    datasets: [{
      label: 'Outbound',
      data: dashboardData?.shipmentsByLocation?.outbound || [145, 189, 125, 98, 87, 76, 65, 112],
      backgroundColor: '#6366f1'
    }, {
      label: 'Inbound',
      data: dashboardData?.shipmentsByLocation?.inbound || [132, 178, 145, 89, 92, 81, 72, 98],
      backgroundColor: '#8b5cf6'
    }]
  };

  const locationChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  // Exception Chart Data
  const exceptionChartData = {
    labels: dashboardData?.exceptions?.labels || ['Delayed', 'Failed', 'Damaged', 'Lost', 'Other'],
    datasets: [{
      data: dashboardData?.exceptions?.values || [42, 18, 12, 5, 8],
      backgroundColor: [
        '#fbbf24',
        '#ef4444',
        '#f97316',
        '#dc2626',
        '#9ca3af'
      ]
    }]
  };

  const exceptionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 10,
          font: {
            size: 11
          }
        }
      }
    }
  };

  // Processing Time Chart Data
  const processingChartData = {
    datasets: [{
      label: 'Processing Times',
      data: dashboardData?.processingTime?.map(item => ({
        x: item.hour,
        y: parseFloat(item.processingTime)
      })) || Array.from({ length: 50 }, () => ({
        x: Math.random() * 24,
        y: Math.random() * 5
      })),
      backgroundColor: 'rgba(99, 102, 241, 0.6)'
    }]
  };

  const processingChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Hour of Day'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Processing Time (min)'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  // Product Trends Chart Data
  const productTrendsData = {
    labels: dashboardData?.productTrends?.labels || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: dashboardData?.productTrends?.datasets?.map((dataset, index) => ({
      label: dataset.label,
      data: dataset.data,
      borderColor: ['#6366f1', '#8b5cf6', '#a855f7'][index] || '#6366f1',
      tension: 0.3
    })) || [{
      label: 'Electronics',
      data: [65, 78, 90, 81, 86, 55, 40],
      borderColor: '#6366f1',
      tension: 0.3
    }, {
      label: 'Apparel',
      data: [45, 52, 48, 58, 62, 48, 35],
      borderColor: '#8b5cf6',
      tension: 0.3
    }, {
      label: 'Food',
      data: [28, 34, 42, 38, 45, 52, 48],
      borderColor: '#a855f7',
      tension: 0.3
    }]
  };

  const productTrendsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          padding: 10,
          font: {
            size: 11
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    }
  };

  // Location Trends Chart Data (Route Performance)
  const locationTrendsData = {
    labels: ['Speed', 'Reliability', 'Volume', 'Cost Efficiency', 'Customer Satisfaction'],
    datasets: dashboardData?.routePerformance?.map((route, index) => ({
      label: route.name,
      data: [
        route.metrics.speed,
        route.metrics.reliability,
        route.metrics.volume,
        route.metrics.costEfficiency,
        route.metrics.customerSatisfaction
      ],
      borderColor: index === 0 ? '#6366f1' : '#8b5cf6',
      backgroundColor: index === 0 ? 'rgba(99, 102, 241, 0.2)' : 'rgba(139, 92, 246, 0.2)'
    })) || [{
      label: 'LA → NY',
      data: [85, 92, 78, 73, 88],
      borderColor: '#6366f1',
      backgroundColor: 'rgba(99, 102, 241, 0.2)'
    }, {
      label: 'Chicago → Miami',
      data: [78, 85, 92, 80, 82],
      borderColor: '#8b5cf6',
      backgroundColor: 'rgba(139, 92, 246, 0.2)'
    }]
  };

  const locationTrendsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          padding: 10,
          font: {
            size: 11
          }
        }
      }
    },
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      }
    }
  };

  // Latency Chart Data (System Performance)
  const latencyChartData = {
    labels: dashboardData?.systemPerformance?.labels || Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [{
      label: 'Average Latency (ms)',
      data: dashboardData?.systemPerformance?.latency || Array.from({ length: 24 }, () => Math.random() * 50 + 20),
      borderColor: '#ef4444',
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      yAxisID: 'y',
      tension: 0.3,
      fill: true
    }, {
      label: 'Throughput (req/s)',
      data: dashboardData?.systemPerformance?.throughput || Array.from({ length: 24 }, () => Math.random() * 500 + 300),
      borderColor: '#10b981',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      yAxisID: 'y1',
      tension: 0.3,
      fill: true
    }]
  };

  const latencyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Latency (ms)'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Throughput (req/s)'
        },
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };

  const handleSearch = () => {
    console.log('Searching for:', searchValue);
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const applyFilters = () => {
    console.log('Applying filters:', filters);
    const newActiveFilters = [];
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        newActiveFilters.push({
          key,
          value: filters[key],
          label: `${key.charAt(0).toUpperCase() + key.slice(1)}: ${filters[key]}`
        });
      }
    });
    setActiveFilters(newActiveFilters);
  };

  const removeFilter = (filterKey) => {
    setFilters({ ...filters, [filterKey]: '' });
    setActiveFilters(activeFilters.filter(f => f.key !== filterKey));
  };


  const openSliceModal = (widgetType) => {
    setActiveSliceFilter(widgetType);
    setShowSliceModal(true);
  };

  const handleFacetedFilters = (filters) => {
    console.log('Faceted filters applied:', filters);
    setFacetedFilters(filters);
    // Apply filters to data here
  };


  const clearFilters = () => {
    setFilters({
      poNumber: '',
      sender: '',
      receiver: '',
      epcCode: '',
      productCode: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Calculate active filter count for badge
  const getActiveFilterCount = () => {
    return Object.values(facetedFilters).reduce((count, category) => {
      if (typeof category === 'object') {
        return count + Object.values(category).filter(item => item && item.checked).length;
      }
      return count;
    }, 0);
  };

  return (
    <>
      {/* Floating Toolbar */}
      <FloatingToolbar
        onOpenFacetedSearch={() => setShowFacetedSearch(true)}
        activeFilterCount={getActiveFilterCount()}
      />

      {/* Top Header */}
      <header className="top-header">
        <div className="breadcrumb">
          <span>Analytics</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Overview</span>
        </div>
      </header>

      {/* Dashboard Content */}
      <div className="dashboard-container">
        {/* Faceted Search Panel */}
        <FacetedSearch
          isOpen={showFacetedSearch}
          onClose={() => setShowFacetedSearch(false)}
          onApplyFilters={handleFacetedFilters}
        />

        <div className="dashboard-header">
          <div className="header-title">
            <h1>Titan Shipment Analytics</h1>
            <p>Real-time monitoring and analysis of your shipping operations</p>
          </div>

        </div>



        {/* Advanced Search & Filter Section */}
        <div className="search-section">
          <div className="search-header">
            <div className="main-search-bar">
              <svg className="main-search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <input
                type="text"
                className="main-search-input"
                placeholder="Search by PO number, EPC, product code, sender, receiver..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </div>
            <div className="search-actions">
              <button className="search-button" onClick={handleSearch}>Search</button>
              <button className="advanced-toggle" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
                <span>Advanced</span>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
            </div>
          </div>

          <div className="saved-searches">
            <div className="saved-search-chip active">All Shipments</div>
            <div className="saved-search-chip">Today&apos;s Deliveries</div>
            <div className="saved-search-chip">Pending Orders</div>
            <div className="saved-search-chip">High Priority</div>
            <div className="saved-search-chip">International</div>
            <div className="saved-search-chip">Express Only</div>
          </div>

          {showAdvancedFilters && (
            <div className="advanced-filters">
              <div className="filter-container">
                <div className="filter-group">
                  <label className="filter-label">Purchase Order</label>
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="e.g., PO-2024-0847"
                    value={filters.poNumber}
                    onChange={(e) => handleFilterChange('poNumber', e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">Sender</label>
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="Company or person name"
                    value={filters.sender}
                    onChange={(e) => handleFilterChange('sender', e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">Receiver</label>
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="Company or person name"
                    value={filters.receiver}
                    onChange={(e) => handleFilterChange('receiver', e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">EPC Code</label>
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="e.g., EPC-123456"
                    value={filters.epcCode}
                    onChange={(e) => handleFilterChange('epcCode', e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">Product Code</label>
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="e.g., SKU-789"
                    value={filters.productCode}
                    onChange={(e) => handleFilterChange('productCode', e.target.value)}
                  />
                </div>

                <div className="filter-group">
                  <label className="filter-label">Status</label>
                  <select
                    className="filter-select"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="delivered">Delivered</option>
                    <option value="transit">In Transit</option>
                    <option value="pending">Pending</option>
                    <option value="delayed">Delayed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <div className="filter-group" style={{ gridColumn: 'span 2' }}>
                  <label className="filter-label">Date Range</label>
                  <div className="date-range-container">
                    <input
                      type="date"
                      className="filter-input"
                      value={filters.dateFrom}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    />
                    <span className="date-separator">to</span>
                    <input
                      type="date"
                      className="filter-input"
                      value={filters.dateTo}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="filter-actions">
                <button className="filter-btn primary" onClick={applyFilters}>Apply Filters</button>
                <button className="filter-btn" onClick={clearFilters}>Clear All</button>
                <button className="filter-btn">Save Search</button>
              </div>

              {activeFilters.length > 0 && (
                <div className="active-filters">
                  {activeFilters.map((filter) => (
                    <div key={filter.key} className="active-filter-chip">
                      <span>{filter.label}</span>
                      <button
                        className="remove-filter"
                        onClick={() => removeFilter(filter.key)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-label">Loading...</div>
              <div className="stat-value">--</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Loading...</div>
              <div className="stat-value">--</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Loading...</div>
              <div className="stat-value">--</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Loading...</div>
              <div className="stat-value">--</div>
            </div>
          </div>
        ) : error ? (
          <div className="stats-row">
            <div className="stat-card" style={{ gridColumn: '1 / -1', textAlign: 'center', color: '#ef4444' }}>
              Error loading stats: {error}
            </div>
          </div>
        ) : (
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-label">Total Shipments</div>
              <div className="stat-value">{dashboardData?.stats?.totalShipments?.value || '0'}</div>
              <div className={`stat-change ${dashboardData?.stats?.totalShipments?.trend === 'up' ? 'positive' : 'negative'}`}>
                <span>{dashboardData?.stats?.totalShipments?.trend === 'up' ? '↑' : '↓'}</span> {dashboardData?.stats?.totalShipments?.change || '0'}% from yesterday
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Average Volume</div>
              <div className="stat-value">{dashboardData?.stats?.averageVolume?.value || '0'}</div>
              <div className={`stat-change ${dashboardData?.stats?.averageVolume?.trend === 'up' ? 'positive' : 'negative'}`}>
                <span>{dashboardData?.stats?.averageVolume?.trend === 'up' ? '↑' : '↓'}</span> {dashboardData?.stats?.averageVolume?.change || '0'}% from last week
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">On-Time Delivery</div>
              <div className="stat-value">{dashboardData?.stats?.onTimeDelivery?.value || '0'}%</div>
              <div className={`stat-change ${dashboardData?.stats?.onTimeDelivery?.trend === 'up' ? 'positive' : 'negative'}`}>
                <span>{dashboardData?.stats?.onTimeDelivery?.trend === 'up' ? '↑' : '↓'}</span> {dashboardData?.stats?.onTimeDelivery?.change || '0'}% from target
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Active Routes</div>
              <div className="stat-value">{dashboardData?.stats?.activeRoutes?.value || '0'}</div>
              <div className={`stat-change ${dashboardData?.stats?.activeRoutes?.trend === 'up' ? 'positive' : 'negative'}`}>
                <span>{dashboardData?.stats?.activeRoutes?.trend === 'up' ? '↑' : '↓'}</span> {dashboardData?.stats?.activeRoutes?.change || '0'} new routes
              </div>
            </div>
          </div>
        )}

        <div className="dashboard-grid">

          {/* Shipment Frequency Chart */}
          <div className="widget widget-1">
            <div className="widget-header">
              <div className="widget-title">Shipment Frequency & Volume</div>
              <div className="widget-actions">
                <span className="widget-action" onClick={() => openSliceModal('frequency')}>🔍</span>
                <span className="widget-action">⬇️</span>
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">Daily shipment trends over the past 30 days</div>
            <div className="chart-container">
              <Line data={frequencyChartData} options={frequencyChartOptions} />
            </div>
          </div>

          {/* Quantity Distribution */}
          <div className="widget widget-2">
            <div className="widget-header">
              <div className="widget-title">Quantity per Shipment</div>
              <div className="widget-actions">
                <span className="widget-action" onClick={() => openSliceModal('quantity')}>🔍</span>
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">Distribution analysis</div>
            <div className="chart-container small">
              <Bar data={quantityChartData} options={quantityChartOptions} />
            </div>
          </div>

          {/* Product Mix */}
          <div className="widget widget-3">
            <div className="widget-header">
              <div className="widget-title">Product Mix</div>
              <div className="widget-actions">
                <span className="widget-action" onClick={() => openSliceModal('product')}>🔍</span>
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">Top product categories</div>
            <div className="chart-container small">
              <Doughnut data={productMixData} options={productMixOptions} />
            </div>
          </div>

          {/* Geographic Distribution */}
          <div className="widget widget-4">
            <div className="widget-header">
              <div className="widget-title">Shipments by Location</div>
              <div className="widget-actions">
                <span className="widget-action" onClick={() => openSliceModal('location')}>🔍</span>
                <span className="widget-action">🗺️</span>
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">Top shipping and receiving locations</div>
            <div className="chart-container">
              <Bar data={locationChartData} options={locationChartOptions} />
            </div>
          </div>

          {/* Top Partners */}
          <div className="widget widget-5">
            <div className="widget-header">
              <div className="widget-title">Top Partners</div>
              <div className="widget-actions">
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">By shipment volume</div>
            <div className="leaderboard">
              {(dashboardData?.topPartners || []).map((partner) => (
                <div key={partner.rank} className="leaderboard-item">
                  <div className={`leaderboard-rank ${partner.rank === 1 ? 'gold' : partner.rank === 2 ? 'silver' : partner.rank === 3 ? 'bronze' : ''}`}>
                    {partner.rank}
                  </div>
                  <div className="leaderboard-info">
                    <div className="leaderboard-name">{partner.name}</div>
                    <div className="leaderboard-stat">{partner.shipments.toLocaleString()} shipments</div>
                  </div>
                  <div className="leaderboard-value">
                    {partner.change >= 0 ? '↑' : '↓'} {Math.abs(partner.change).toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Exception Chart */}
          <div className="widget widget-6">
            <div className="widget-header">
              <div className="widget-title">Exception Analysis</div>
              <div className="widget-actions">
                <span className="widget-action" onClick={() => openSliceModal('exceptions')}>🔍</span>
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">Last 7 days</div>
            <div className="chart-container small">
              <Pie data={exceptionChartData} options={exceptionChartOptions} />
            </div>
          </div>

          {/* Processing Time Chart */}
          <div className="widget widget-7">
            <div className="widget-header">
              <div className="widget-title">Processing Time Distribution</div>
              <div className="widget-actions">
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">Hourly analysis</div>
            <div className="chart-container small">
              <Scatter data={processingChartData} options={processingChartOptions} />
            </div>
          </div>

          {/* Product Trends */}
          <div className="widget widget-8">
            <div className="widget-header">
              <div className="widget-title">Product Trends</div>
              <div className="widget-actions">
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">Weekly comparison</div>
            <div className="chart-container small">
              <Line data={productTrendsData} options={productTrendsOptions} />
            </div>
          </div>

          {/* Location Trends */}
          <div className="widget widget-9">
            <div className="widget-header">
              <div className="widget-title">Route Performance</div>
              <div className="widget-actions">
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">Top routes analysis</div>
            <div className="chart-container small">
              <Radar data={locationTrendsData} options={locationTrendsOptions} />
            </div>
          </div>

          {/* System Metrics */}
          <div className="widget widget-10">
            <div className="widget-header">
              <div className="widget-title">System Performance</div>
              <div className="widget-actions">
                <span className="widget-action">⚙️</span>
              </div>
            </div>
            <div className="widget-subtitle">Latency & Throughput</div>
            <div className="chart-container">
              <Line data={latencyChartData} options={latencyChartOptions} />
            </div>
          </div>

          {/* Metabase Widget - RFxcel Shipments */}
          <div className="widget widget-11">
            <MetabaseWidget
              questionId={113}
              title="RFxcel Shipments Count"
              subtitle="Grouped by shipment event time (monthly)"
            />
          </div>
        </div>

        {/* Recent Shipments Table */}
        <div className="widget full-width">
          <div className="widget-header">
            <div className="widget-title">Recent Shipments</div>
            <div className="widget-actions">
              <button className="action-btn">Export CSV</button>
              <button className="action-btn">View All</button>
            </div>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Shipment ID</th>
                  <th>Origin</th>
                  <th>Destination</th>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Status</th>
                  <th>ETA</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(dashboardData?.recentShipments?.shipments || []).map((shipment) => (
                  <tr key={shipment.id}>
                    <td>#{shipment.id.substring(0, 13)}...</td>
                    <td>{shipment.origin}</td>
                    <td>{shipment.destination}</td>
                    <td>{shipment.product}</td>
                    <td>{shipment.quantity}</td>
                    <td>
                      <span className={`status-badge ${shipment.status.toLowerCase().replace(' ', '-')}`}>
                        {shipment.status}
                      </span>
                    </td>
                    <td>{new Date(shipment.eta).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td>
                      <button className="table-action">Track</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Slice and Dice Modal */}
      {showSliceModal && (
        <div className="modal-overlay" onClick={() => setShowSliceModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{getSliceModalTitle(activeSliceFilter)}</h3>
              <button className="modal-close" onClick={() => setShowSliceModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {renderSliceModalContent(activeSliceFilter)}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowSliceModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={() => {
                // Apply slice filters
                setShowSliceModal(false);
              }}>Apply Filters</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

function getSliceModalTitle(filterType) {
  const titles = {
    frequency: 'Refine Shipment Frequency & Volume',
    quantity: 'Refine Quantity Analysis',
    product: 'Refine Product Mix Analysis',
    location: 'Refine Location Analysis',
    exceptions: 'Exception & Alert Filters'
  };
  return titles[filterType] || 'Filters';
}

function renderSliceModalContent(filterType) {
  switch (filterType) {
    case 'frequency':
      return (
        <>
          <div className="dynamic-filter-section">
            <h4>Time Granularity</h4>
            <div className="quick-filters">
              <button className="quick-filter-chip selected">Daily</button>
              <button className="quick-filter-chip">Weekly</button>
              <button className="quick-filter-chip">Monthly</button>
              <button className="quick-filter-chip">Quarterly</button>
              <button className="quick-filter-chip">Hourly</button>
            </div>
          </div>
          <div className="dynamic-filter-section">
            <h4>Volume Range (Shipments per day)</h4>
            <div className="range-slider-container">
              <div className="range-slider-label">
                <span>Min: 0</span>
                <span>Max: 500</span>
              </div>
              <input type="range" className="range-slider" min="0" max="1000" defaultValue="500" />
            </div>
          </div>
          <div className="dynamic-filter-section">
            <h4>Shipment Categories</h4>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input type="checkbox" id="cat-express" defaultChecked />
                <label htmlFor="cat-express">Express Delivery</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="cat-standard" defaultChecked />
                <label htmlFor="cat-standard">Standard Shipping</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="cat-freight" defaultChecked />
                <label htmlFor="cat-freight">Freight</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="cat-international" />
                <label htmlFor="cat-international">International</label>
              </div>
            </div>
          </div>
        </>
      );
    case 'quantity':
      return (
        <>
          <div className="dynamic-filter-section">
            <h4>Quantity Ranges</h4>
            <div className="quick-filters">
              <button className="quick-filter-chip selected">Small (1-50)</button>
              <button className="quick-filter-chip">Medium (51-200)</button>
              <button className="quick-filter-chip">Large (201-500)</button>
              <button className="quick-filter-chip">Bulk (500+)</button>
            </div>
          </div>
          <div className="dynamic-filter-section">
            <h4>Weight Categories</h4>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input type="checkbox" id="weight-light" defaultChecked />
                <label htmlFor="weight-light">Light (&lt; 10 kg)</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="weight-medium" defaultChecked />
                <label htmlFor="weight-medium">Medium (10-50 kg)</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="weight-heavy" defaultChecked />
                <label htmlFor="weight-heavy">Heavy (50-200 kg)</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="weight-freight" />
                <label htmlFor="weight-freight">Freight (&gt; 200 kg)</label>
              </div>
            </div>
          </div>
        </>
      );
    case 'product':
      return (
        <>
          <div className="dynamic-filter-section">
            <h4>Product Categories</h4>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input type="checkbox" id="prod-electronics" defaultChecked />
                <label htmlFor="prod-electronics">Electronics</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="prod-apparel" defaultChecked />
                <label htmlFor="prod-apparel">Apparel</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="prod-food" defaultChecked />
                <label htmlFor="prod-food">Food & Beverage</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="prod-industrial" defaultChecked />
                <label htmlFor="prod-industrial">Industrial</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="prod-pharma" />
                <label htmlFor="prod-pharma">Pharmaceuticals</label>
              </div>
            </div>
          </div>
          <div className="dynamic-filter-section">
            <h4>Special Handling</h4>
            <div className="quick-filters">
              <button className="quick-filter-chip">Fragile</button>
              <button className="quick-filter-chip">Hazmat</button>
              <button className="quick-filter-chip">Refrigerated</button>
              <button className="quick-filter-chip">Oversized</button>
            </div>
          </div>
        </>
      );
    case 'location':
      return (
        <>
          <div className="dynamic-filter-section">
            <h4>Region Filter</h4>
            <div className="quick-filters">
              <button className="quick-filter-chip selected">All Regions</button>
              <button className="quick-filter-chip">North America</button>
              <button className="quick-filter-chip">South America</button>
              <button className="quick-filter-chip">Europe</button>
              <button className="quick-filter-chip">Asia Pacific</button>
              <button className="quick-filter-chip">Africa</button>
            </div>
          </div>
          <div className="dynamic-filter-section">
            <h4>Route Types</h4>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input type="checkbox" id="route-domestic" defaultChecked />
                <label htmlFor="route-domestic">Domestic</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="route-international" defaultChecked />
                <label htmlFor="route-international">International</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="route-crossborder" defaultChecked />
                <label htmlFor="route-crossborder">Cross-border</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="route-lastmile" />
                <label htmlFor="route-lastmile">Last Mile</label>
              </div>
            </div>
          </div>
        </>
      );
    case 'exceptions':
      return (
        <>
          <div className="dynamic-filter-section">
            <h4>Exception Types</h4>
            <div className="checkbox-group">
              <div className="checkbox-item">
                <input type="checkbox" id="ex-delayed" defaultChecked />
                <label htmlFor="ex-delayed">Delayed Shipments</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="ex-failed" defaultChecked />
                <label htmlFor="ex-failed">Failed Deliveries</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="ex-damaged" defaultChecked />
                <label htmlFor="ex-damaged">Damaged Goods</label>
              </div>
              <div className="checkbox-item">
                <input type="checkbox" id="ex-lost" defaultChecked />
                <label htmlFor="ex-lost">Lost Packages</label>
              </div>
            </div>
          </div>
          <div className="dynamic-filter-section">
            <h4>Severity Level</h4>
            <div className="quick-filters">
              <button className="quick-filter-chip">Critical</button>
              <button className="quick-filter-chip selected">High</button>
              <button className="quick-filter-chip selected">Medium</button>
              <button className="quick-filter-chip">Low</button>
            </div>
          </div>
        </>
      );
    default:
      return null;
  }
}

export default Dashboard;